import { RobotErrorCode } from '../utils/constants';

export type SlotFilenameSlotInfo = {
  deviceFilename?: string;
  programTitle?: string;
};

const EMPTY_SLOT_FILENAME = 'empty.py';
export const MAX_SLOT_DEVICE_FILENAME_UTF8_BYTES = 63;

export class SlotDeviceFilenameValidationError extends Error {
  readonly code = RobotErrorCode.INVALID_SLOT_DEVICE_FILENAME;

  constructor() {
    super('Invalid Python filename');
    this.name = 'SlotDeviceFilenameValidationError';
  }
}

function getUtf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/** True when `filename` is a Python basename with no path traversal or path segments. */
export function isTrustedSlotDeviceFilename(filename: string): boolean {
  const trimmed = filename.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('\0')) {
    return false;
  }

  if (trimmed.toLowerCase() === EMPTY_SLOT_FILENAME) {
    return true;
  }

  if (!trimmed.endsWith('.py')) {
    return false;
  }

  const baseName = trimmed.slice(0, -3);
  return baseName.trim().length > 0 && getUtf8ByteLength(trimmed) <= MAX_SLOT_DEVICE_FILENAME_UTF8_BYTES;
}

/** Reject filenames that cannot be safely deployed as Drive Unit Python assets. */
export function assertDeployableSlotDeviceFilename(filename: string): void {
  if (!isTrustedSlotDeviceFilename(filename) || filename.trim().toLowerCase() === EMPTY_SLOT_FILENAME) {
    throw new SlotDeviceFilenameValidationError();
  }
}

/** Derive and validate the exact Python filename sent for a BPS project deploy. */
export function projectNameToSlotDeviceFilename(projectName: string): string {
  const normalizedProjectName = projectName.trim();
  const filename = normalizedProjectName.endsWith('.py') ? normalizedProjectName : `${normalizedProjectName}.py`;
  assertDeployableSlotDeviceFilename(filename);

  return filename;
}

/**
 * Normalize a device-reported slot filename before storing it in Redux.
 * Returns `empty.py` for empty slots, a trusted basename for occupied slots, or `null` when rejected.
 */
export function sanitizeDeviceReportedSlotFilename(filename: string | null | undefined): string | null {
  if (filename == null) {
    return null;
  }

  const trimmed = filename.trim();
  if (trimmed === '' || trimmed.toLowerCase() === EMPTY_SLOT_FILENAME) {
    return EMPTY_SLOT_FILENAME;
  }

  return isTrustedSlotDeviceFilename(trimmed) ? trimmed : null;
}

function sanitizeProgramTitleFilename(programTitle: string, fallbackSlot: string): string {
  const baseTitle = programTitle.replace(/\.[^/.]+$/, '');
  const safeBaseName = baseTitle.replace(/[^A-Za-z0-9_-]/g, '_') || fallbackSlot;
  return `${safeBaseName}.py`;
}

/**
 * Resolve the Python filename for a drive-unit slot from cached metadata.
 * Untrusted `deviceFilename` values fall back to a sanitized project title or slot name.
 */
export function normalizeSlotFilename(slotInfo: SlotFilenameSlotInfo | null | undefined, fallbackSlot: string): string {
  const deviceFilename = typeof slotInfo?.deviceFilename === 'string' ? slotInfo.deviceFilename.trim() : '';
  if (deviceFilename && isTrustedSlotDeviceFilename(deviceFilename)) {
    return deviceFilename.toLowerCase() === EMPTY_SLOT_FILENAME ? EMPTY_SLOT_FILENAME : deviceFilename;
  }

  const programTitle = typeof slotInfo?.programTitle === 'string' ? slotInfo.programTitle.trim() : '';
  if (programTitle) {
    return sanitizeProgramTitleFilename(programTitle, fallbackSlot);
  }

  return sanitizeProgramTitleFilename('', fallbackSlot);
}
